import { useEffect, useState } from 'react';
import { useAppSelector } from '../hooks';
import { getStore } from './storeReference';
import { registerFeatureModule, isFeatureRegistered } from './registry';
import { FeatureModule } from './types';

/**
 * Hook to ensure a feature is registered before using it
 * @param featureModule The feature module to register
 * @returns Whether the feature is registered successfully
 */
export function useFeature(featureModule: FeatureModule): boolean {
  const isRegistered = useAppSelector(state => isFeatureRegistered(state, featureModule.id));
  const [isRegistering, setIsRegistering] = useState(false);
  const [registrationFailed, setRegistrationFailed] = useState(false);

  useEffect(() => {
    if (!isRegistered && !isRegistering && !registrationFailed) {
      setIsRegistering(true);

      try {
        const store = getStore();
        const result = registerFeatureModule(featureModule, store);

        if (!result.success) {
          console.error(`Failed to register feature ${featureModule.id}:`, result.error);
          setRegistrationFailed(true);
        }
      } catch (error) {
        console.error(`Error registering feature ${featureModule.id}:`, error);
        setRegistrationFailed(true);
      } finally {
        setIsRegistering(false);
      }
    }
  }, [featureModule, isRegistered, isRegistering, registrationFailed]);

  return isRegistered;
}

/**
 * Hook to check if a feature is registered
 * @param featureId The ID of the feature to check
 * @returns Whether the feature is registered
 */
export function useIsFeatureRegistered(featureId: string): boolean {
  return useAppSelector(state => isFeatureRegistered(state, featureId));
}

/**
 * Hook to ensure multiple features are registered before using them
 * @param featureIds Array of feature IDs that need to be registered
 * @returns Whether all features are registered
 */
export function useRequireFeatures(featureIds: string[]): boolean {
  const registeredFeatures = useAppSelector(state =>
    featureIds.every(id => isFeatureRegistered(state, id)),
  );

  return registeredFeatures;
}
